import { Text } from "components/Typography/text.styled";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import {
  Cell,
  Container,
  DeductionAmountContainer,
  DividerLine1,
  DividerLine2,
  DividerLine3,
  EmptyDiv,
  GrossSalaryContainer,
  HoursContainer,
  Logo,
  LogoContainer,
  NetSalaryContainer,
  PayrollTaxContainer,
  Row,
  SalaryActionContainer,
  SalaryNoticeContainer,
  SalaryPageContainer,
  TaxContainer,
  TaxTableContainer,
  TooltipContainer,
  TooltipModal,
  WorkerContainer,
} from "./salary.styled";
import { FontFamily, FontSize } from "config/font";
import { SalaryNotice } from "model/Salary";
import { Color } from "config/colors";
import { IconCircularContainer } from "../dashboard/dashboard.styled";
import { CustomIcon } from "components/icon/customIcon.component";
import { formatDuration } from "utils/utils";
import { WorkerDropdown } from "./workerDropDown.component";
import { Api } from "services/api/api.service";
import { Dropdown } from "components/dropdown/Dropdown";
import { SummaryIndividualSalary } from "./summaryIndividualSalary.component";
import { PaymentMethod } from "model/Gig";
import SimflexityPayLogo from "images/logo/svg/simflexityPay-logo.svg";
import { FileViewer } from "../fileViewer/fileViewer.component";
import { LinkWrapper } from "../gig/gigDetail/gigDetail.styled";
import Modal from "@material-ui/core/Modal/Modal";
import { CompanyWorker } from "model/Company";
import { FilterContainer } from "../gig/gigList/gigs.styled";

export const formatTimePeriod = (timePeriod: string): string => {
  const year = timePeriod.slice(0, 4);
  const month = timePeriod.slice(4, 6);

  return `${year}-${month}`;
};

export interface ModalState {
  open: boolean;
  workerId?: number;
  pdfId?: number;
  period?: string;
  workerName?: string;
}

export const IndividualSalary = () => {
  const { t } = useTranslation();
  const [{ company }] = useCompanyStore();
  const [salaryNotices, setSalaryNotices] = useState<SalaryNotice[]>([]);
  const [selectedWorker, setSelectedWorker] = useState<CompanyWorker>();
  const [selectedYear, setSelectedYear] = useState<number | undefined>();
  const [showInfo, setShowInfo] = useState(false);
  const [modalState, setModalState] = useState<ModalState>({
    open: false,
    workerId: undefined,
    pdfId: undefined,
    period: undefined,
    workerName: undefined,
  });
  const [years, setYears] = useState<number[]>([]);

  useEffect(() => {
    const startYear = 2024;
    const currentYear = new Date().getFullYear();
    const yearsList: number[] = [];

    for (let year = currentYear; year >= startYear; year--) {
      yearsList.push(year);
    }

    setYears(yearsList);
  }, []);

  const yearDropdownItems = useMemo(() => {
    const items = [{ value: "", label: t("Salary.Year") }];

    years.forEach((year) => {
      items.push({ value: year.toString(), label: year.toString() });
    });

    return items;
  }, [years, t]);

  const getSalaries = useCallback(() => {
    if (!company?.id || !selectedWorker) return;

    Api()
      .company.salary.getWorkerSalaries(
        company.id,
        selectedWorker.id,
        selectedYear
      )
      .then((res) => {
        setSalaryNotices(res.data.salaryNotices);
      })
      .catch((e) => {
        console.error("Error when getting salaries", e);
      });
  }, [company?.id, selectedWorker, selectedYear]);

  const openSalaryPdf = useCallback((salary: SalaryNotice) => {
    setModalState({
      open: true,
      workerId: salary.workerId,
      pdfId: salary.id,
      period: salary.period,
      workerName: salary.workerName,
    });
  }, []);

  const closeModal = useCallback(() => {
    setModalState((prev) => ({ ...prev, open: false }));
  }, []);

  useEffect(() => {
    if (modalState.pdfId && !modalState.open) {
      setModalState((prev) => ({ ...prev, open: true }));
    }
  }, [modalState.pdfId]);

  useEffect(() => {
    if (!modalState.open) {
      setModalState((prev) => ({ ...prev, pdfId: undefined }));
    }
  }, [modalState.open]);

  useEffect(() => {
    getSalaries();
  }, [getSalaries]);

  const renderSalaryNotices = useCallback(() => {
    if (salaryNotices.length === 0) {
      return (
        <Text style={{ textAlign: "center", marginTop: 40 }}>
          {t("Salary.NoSalaryDataForPerson")}
        </Text>
      );
    }

    return (
      <>
        <SalaryNoticeContainer>
          {salaryNotices.map((salary, index) => {
            const currency = salary.currency === "SEK" ? " kr" : "";
            const isLastItem = index === salaryNotices.length - 1;

            return (
              <Cell
                style={{ marginBottom: isLastItem ? 0 : 10 }}
                key={`salary-${salary.id}-${index}`}
              >
                <WorkerContainer>
                  <Text
                    style={{ margin: 0, padding: 0 }}
                    fontSize={FontSize.Small}
                    color={Color.SeaBlue600}
                  >
                    {t("Salary.Period")}
                  </Text>
                  <Text
                    color={Color.MidnightBlue}
                    fontFamily={FontFamily.MontserratRegular}
                  >
                    {formatTimePeriod(salary.period)}
                  </Text>
                </WorkerContainer>
                <DividerLine1 />
                <HoursContainer>
                  <Text
                    style={{ margin: 0, padding: 0 }}
                    fontSize={FontSize.Small}
                    color={Color.SeaBlue600}
                  >
                    {t("Salary.TotalWorkTime")}
                  </Text>
                  <Text
                    color={Color.MidnightBlue}
                    fontFamily={FontFamily.MontserratRegular}
                  >
                    {formatDuration(salary.workedHours.toString())}
                  </Text>
                </HoursContainer>
                <DividerLine2 />
                <GrossSalaryContainer>
                  <Text
                    style={{ margin: 0, padding: 0 }}
                    fontSize={FontSize.Small}
                    color={Color.SeaBlue600}
                  >
                    {t("Salary.GrossSalary")}
                  </Text>
                  <Text
                    color={Color.MidnightBlue}
                    fontFamily={FontFamily.MontserratRegular}
                  >
                    {`${salary.grossSalary}${currency}`}
                  </Text>
                </GrossSalaryContainer>
                <NetSalaryContainer>
                  <Text
                    style={{ margin: 0, padding: 0 }}
                    fontSize={FontSize.Small}
                    color={Color.SeaBlue600}
                  >
                    {t("Salary.NetSalary")}
                  </Text>
                  <Text
                    color={Color.MidnightBlue}
                    fontFamily={FontFamily.MontserratRegular}
                  >
                    {`${salary.netSalary}${currency}`}
                  </Text>
                </NetSalaryContainer>
                <TaxContainer>
                  <Text
                    style={{ margin: 0, padding: 0 }}
                    fontSize={FontSize.Small}
                    color={Color.SeaBlue600}
                  >
                    {t("Salary.Tax")}
                  </Text>
                  <Text
                    color={Color.MidnightBlue}
                    fontFamily={FontFamily.MontserratRegular}
                  >
                    {`${salary.preliminaryTax}${currency}`}
                  </Text>
                </TaxContainer>
                <PayrollTaxContainer>
                  <Text
                    style={{ margin: 0, padding: 0 }}
                    fontSize={FontSize.Small}
                    color={Color.SeaBlue600}
                  >
                    {t("Salary.PayrollTax")}
                  </Text>
                  <Text
                    color={Color.MidnightBlue}
                    fontFamily={FontFamily.MontserratRegular}
                  >
                    {`${salary.payrollTax}${currency}`}
                  </Text>
                </PayrollTaxContainer>
                <DeductionAmountContainer>
                  <Text
                    style={{ margin: 0, padding: 0 }}
                    fontSize={FontSize.Small}
                    color={Color.SeaBlue600}
                  >
                    {t("Salary.DeductionAmount")}
                  </Text>
                  <Text
                    color={Color.MidnightBlue}
                    fontFamily={FontFamily.MontserratRegular}
                  >
                    {`${salary.deductionAmount}${currency}`}
                  </Text>
                </DeductionAmountContainer>
                <TaxTableContainer>
                  <Text
                    style={{ margin: 0, padding: 0 }}
                    fontSize={FontSize.Small}
                    color={Color.SeaBlue600}
                  >
                    {salary.payrollType === PaymentMethod.SIMFLEXITY_PAY
                      ? t("Salary.TaxProcent")
                      : t("Salary.TaxTable")}
                  </Text>
                  <Text
                    color={Color.MidnightBlue}
                    fontFamily={FontFamily.MontserratRegular}
                  >
                    {salary.payrollType === PaymentMethod.SIMFLEXITY_PAY
                      ? salary.taxPercentage + "%"
                      : salary.taxTable}
                  </Text>
                </TaxTableContainer>
                <DividerLine3 />
                <SalaryActionContainer>
                  <IconCircularContainer
                    isAccept
                    onClick={() => openSalaryPdf(salary)}
                  >
                    <CustomIcon
                      className="icon"
                      name="document"
                      size="20px"
                      padding="2px"
                      color={Color.White}
                    />
                  </IconCircularContainer>
                  {salary.payrollType === PaymentMethod.SIMFLEXITY_PAY ? (
                    <LogoContainer>
                      <Logo src={SimflexityPayLogo} alt="SimflexityPay logo" />
                    </LogoContainer>
                  ) : (
                    <EmptyDiv />
                  )}
                </SalaryActionContainer>
              </Cell>
            );
          })}
        </SalaryNoticeContainer>
        {selectedYear && salaryNotices.length > 0 && (
          <SummaryIndividualSalary
            salaryNotices={salaryNotices}
            selectedYear={selectedYear}
            setSelectedYear={setSelectedYear}
          />
        )}
      </>
    );
  }, [salaryNotices, selectedYear, t, openSalaryPdf]);

  const renderPdfModal = useCallback(() => {
    const { open, workerId, pdfId, period, workerName } = modalState;

    if (!open || !company || !workerId || !pdfId || !period || !workerName) {
      return null;
    }

    return (
      <Modal
        open={open}
        onClose={closeModal}
        aria-labelledby="file-viewer-modal"
        aria-describedby="file-viewer-description"
      >
        <>
          <LinkWrapper>
            <CustomIcon
              color={Color.LighterDestructive}
              onClick={closeModal}
              name="cross"
            >
              {t("General.Close")}
            </CustomIcon>
          </LinkWrapper>
          <FileViewer
            fetchEndpoint={() =>
              Api().company.salary.generateSalaryNotice(
                company.id,
                workerId,
                pdfId
              )
            }
            filename={`SalaryNotice_${period}_${workerName}.pdf`}
            type="application/pdf"
          />
        </>
      </Modal>
    );
  }, [modalState, company, closeModal, t]);

  return (
    <>
      <div
        style={{ display: "flex", justifyContent: "flex-end", width: "100%" }}
      >
        <TooltipContainer>
          <Row
            onMouseEnter={() => setShowInfo(true)}
            onMouseLeave={() => setShowInfo(false)}
            style={{ cursor: "pointer" }}
          >
            <Text
              fontFamily={FontFamily.MontserratSemiBold}
              fontSize={FontSize.Small}
            >
              {t(`Salary.WhatIs`, {
                name: t("Salary.IndividualSalary").toLowerCase(),
              })}
            </Text>
          </Row>

          {showInfo && (
            <TooltipModal
              onMouseEnter={() => setShowInfo(true)}
              onMouseLeave={() => setShowInfo(false)}
            >
              <Text fontSize={FontSize.Small}>
                {t("Salary.IndividualSalarySlipInfo")}
              </Text>
            </TooltipModal>
          )}
        </TooltipContainer>
      </div>
      <FilterContainer>
        <WorkerDropdown
          selectedWorker={selectedWorker}
          setSelectedWorker={setSelectedWorker}
          onlyActiveWorkers={false}
        />
        <Container style={{ marginLeft: 20 }}>
          <Dropdown
            isCalendar
            textColor={Color.MidnightBlue}
            smallerFont
            placeholder={t("Salary.Year")}
            activeValue={selectedYear?.toString() ?? ""}
            items={yearDropdownItems}
            maxHeight={200}
            onChange={(e: { value: number | string }) => {
              setSelectedYear(e.value ? Number(e.value) : undefined);
            }}
          />
        </Container>
      </FilterContainer>
      <SalaryPageContainer style={{ marginBottom: 80 }}>
        {renderSalaryNotices()}
      </SalaryPageContainer>
      {renderPdfModal()}
    </>
  );
};
